import React, { useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import SelectMultipleChip from "../select-multiple";
import gql from "graphql-tag";
import { useQuery, QueryResponseHandler } from "components/graphql";
import Select from "../select";
import { useCurrentCustomerContext } from "components/current-customer-context";
import { useAuthDataContext } from "components/auth";
import { DEFAULT_SELECT_LIMIT } from "../utils";
import isEqual from "lodash/isEqual";
import isMatch from "lodash/isMatch";

const getQuery = ({
  queryString,
  customerId,
  hasAdminPrivileges,
  username,
  isFirstRender,
  skipOnFirstRender,
  skip,
  queryParams,
}) => ({
  query: gql`
    ${queryString}
  `,
  skip: (skipOnFirstRender && isFirstRender) || skip || (!customerId && !hasAdminPrivileges),
  variables: { offset: 0, limit: DEFAULT_SELECT_LIMIT, customerId, username, ...queryParams },
});

const DependentEntitiesSelector = ({
  queryString,
  offset = 0,
  limit = 5,
  onGetDataFromResponse,
  multiple = false,
  onChange,
  value = multiple ? [] : "",
  onGetValue,
  onGetLabel,
  manuallyScopedQuery,
  queryParams,
  skip,
  getDefaultItemSelected,
  skipOnFirstRender = true,
  onLoadQuery,
  defaultEmptyData = { items: [], total: 0 },
  ...props
}) => {
  const isFirstRender = useRef(true);
  const lastQueryParams = useRef({});
  const wasAlreadySet = useRef(false);
  const { user, hasAdminPrivileges } = useAuthDataContext();
  const { currentCustomerId } = useCurrentCustomerContext();

  const SelectorComponent = multiple ? SelectMultipleChip : Select;
  const handleChange = ({ target: { value } }) => {
    !value.length ? onChange({ id: null }) : onChange({ id: value });
    }
  const handleMultipleChange = ({ target: { value } }) => {
    onChange(value);
  };

  const query = useMemo(
    () =>
      getQuery({
        queryString,
        customerId: hasAdminPrivileges && !manuallyScopedQuery ? null : currentCustomerId,
        hasAdminPrivileges,
        username: user.sub,
        isFirstRender: isFirstRender.current,
        skip,
        queryParams,
        skipOnFirstRender,
      }),
    [
      queryString,
      hasAdminPrivileges,
      manuallyScopedQuery,
      currentCustomerId,
      user.sub,
      skip,
      queryParams,
      skipOnFirstRender,
    ],
  );

  const { data, loading, error, calledVariables } = useQuery(query);
  const extractedData = onGetDataFromResponse(data);
  const elements = extractedData ? extractedData : defaultEmptyData;

  const getValueFromElement = useCallback(element => onGetValue(element), [onGetValue]);
  const getLabelFromElement = useCallback(element => onGetLabel(element), [onGetLabel]);

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  useEffect(
    () => {
      if (onLoadQuery && !loading) {
        onLoadQuery(data, error);
      }
    },
    [data, elements, error, loading, onLoadQuery],
  );

  useEffect(
    () => {
      if (getDefaultItemSelected && !isEqual(lastQueryParams.current, queryParams)) {
        wasAlreadySet.current = false;
        lastQueryParams.current = queryParams;
      }
    },
    [getDefaultItemSelected, queryParams],
  );

  useEffect(
    () => {
      if (getDefaultItemSelected && !wasAlreadySet.current && isMatch(calledVariables, queryParams) && !loading) {
        const defaultItem = getDefaultItemSelected(elements);
        if (defaultItem) {
          wasAlreadySet.current = true;
          onChange({ id: typeof defaultItem === "string" ? defaultItem : defaultItem.id });
        }
      
      }
    },
    [calledVariables, elements, getDefaultItemSelected, loading, onChange, queryParams],
  );

  const getSelectElements = () => {
    if (Array.isArray(elements)) {
      return elements;
    }

    return elements && elements.items ? elements.items : [];
  };

  const selectProps = {
    value,
    onChange: !multiple ? handleChange : handleMultipleChange,
    getValueFromElement,
    getLabelFromElement,
    elements: getSelectElements(elements),
    ...props,
  };

  return (
    <QueryResponseHandler error={error} loading={loading} elements={elements}>
      <SelectorComponent {...selectProps} />
    </QueryResponseHandler>
  );
};

DependentEntitiesSelector.propTypes = {
  /**
   * Parámetros de la query (adicionales al customerId)
   */
  queryParams: PropTypes.object,
  /**
   * Indica si se skipea la query
   */
  skip: PropTypes.bool,
  /**
   * Función que escoge el valor por defecto para el selector
   */
  getDefaultItemSelected: PropTypes.func,
  /**
   * Indica si la query de superadmin se ejecuta en un contexto de selección manual del customer
   **/
  manuallyScopedQuery: PropTypes.bool,
  /**
   * En caso de selector no múltiple, representa el valor del texto
   * de la opción vacía, si existe (opcional)
   */
  emptyValueText: PropTypes.string,
  /**
   * Query GraphQL
   */
  queryString: PropTypes.string.isRequired,
  /**
   * Offset enviado a la query GraphQL paginada
   */
  offset: PropTypes.number,
  /**
   * Limit enviado a la query GraphQL paginada
   */
  limit: PropTypes.number,
  /**
   * Función que recupera el objeto de datos paginados a mostrar a partir del resultado de la query
   */
  onGetDataFromResponse: PropTypes.func.isRequired,
  /**
   * Indica si el selector es múltiple o no
   */
  multiple: PropTypes.bool,
  /**
   * Indica si se skipea la query en el primer render
   */
  skipOnFirstRender: PropTypes.bool,
  /**
   * Etiqueta opcional para el campo
   */
  label: PropTypes.string,
  /**
   * Id del input
   */
  id: PropTypes.string,
  /**
   * Nombre del input
   */
  name: PropTypes.string,
  /**
   * Clase adicional del componente.
   */
  className: PropTypes.string,
  /**
   * Función que se ejecuta al cambiar el valor del campo
   */
  onChange: PropTypes.func,
  /**
   * Valor actual del selector
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  /**
   * Función que obtiene el valor de la opción del selector a partir de un elemento
   */
  onGetValue: PropTypes.func.isRequired,
  /**
   * Función que obtiene el label de la opción del selector a partir de un elemento
   */
  onGetLabel: PropTypes.func.isRequired,
  /**
   * Callback que se llama al terminar de cargar la query
   */
  onLoadQuery: PropTypes.func,
  /**
   * Datos por defecto en caso de no haber datos de la query
   */
  defaultEmptyData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

export default DependentEntitiesSelector;
